import React, { useState, useEffect } from "react";
import "../style/InsertProduct.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FiBox, FiPlus, FiArrowLeft, FiCheck, FiTrash2, FiTruck, FiRotateCcw } from "react-icons/fi";
import { getAllGoods } from "../api/goodsApi";

const InsertProduct = ({ onProductsAdded, onClose, isInModal = true }) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([
    { id: Date.now(), product: "", type: "", delivered: 0, received: 0, srNo: 1, goodsItemId: null },
  ]);

  const [goods, setGoods] = useState([]);
  const [goodsLoading, setGoodsLoading] = useState(true);

  // Fetch goods from API
  useEffect(() => {
    const fetchGoods = async () => {
      try {
        setGoodsLoading(true);
        const response = await getAllGoods();
        setGoods(response.data?.data || response.data || []);
      } catch (error) {
        console.error("Error fetching goods:", error);
        toast.error("Failed to load products");
      } finally {
        setGoodsLoading(false);
      }
    };
    fetchGoods();
  }, []);

  const handleDone = () => {
    const validRows = rows.filter(
      (r) => r.product && (r.delivered > 0 || r.received > 0)
    );

    if (validRows.length === 0) {
      toast.error("Please add at least one product.");
      return;
    }

    if (onProductsAdded) {
      onProductsAdded(validRows);
    }

    toast.success("Product is added successfully!");

    if (onClose) {
      onClose();
    } else {
      navigate("/dashboard", {
        state: { products: validRows },
      });
    }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/dashboard");
    }
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        id: Date.now(),
        product: "",
        type: "",
        delivered: 0,
        received: 0,
        srNo: rows.length + 1,
        goodsItemId: null,
      },
    ]);
  };

  const handleInputChange = (id, field, value) => {
    setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleRemoveRow = (id) => {
    const filtered = rows.filter((r) => r.id !== id);
    const reNumbered = filtered.map((r, idx) => ({ ...r, srNo: idx + 1 }));
    setRows(reNumbered);
  };

  const canDelete = rows.length > 1;

  return (
    <div className="insert-product-container">
      <div className="insert-product-card">
        {/* ===== HEADER ===== */}
        <div className="insert-product-header">
          <div className="insert-product-header-left">
            <div className="insert-product-avatar">
              <FiBox size={20} />
            </div>
            <div className="insert-product-details">
              <h2 className="insert-product-title">Insert Products</h2>
              <p className="insert-product-subtitle">
                Add multiple products for this challan in one go
                {!goodsLoading && goods.length > 0 && (
                  <span className="insert-product-count-badge">{goods.length} available</span>
                )}
              </p>
            </div>
          </div>
          <div className="top-buttons">
            <button type="button" className="btn-back" onClick={handleBack}>
              <FiArrowLeft size={14} /> Back
            </button>
            <button type="button" className="btn-done" onClick={handleDone}>
              <FiCheck size={14} /> Done
            </button>
          </div>
        </div>

        {/* ===== TABLE SECTION ===== */}
        <div className="table-wrapper">
          <div className="header-row">
            <span></span>
            <span>Product</span>
            <span>Type</span>
            <span>Delivered</span>
            <span>Received</span>
            <span></span>
          </div>

          {rows.map((row, idx) => (
            <div className="data-row" key={row.id}>
              <span className="row-index">{idx + 1}</span>

              <select
                className="input-select"
                value={row.product}
                onChange={(e) => {
                  const selectedGood = goods.find((g) => g.name === e.target.value);
                  handleInputChange(row.id, "product", e.target.value);
                  if (selectedGood) {
                    handleInputChange(row.id, "goodsItemId", selectedGood.id);
                  }
                }}
                disabled={goodsLoading}
              >
                <option value="">
                  {goodsLoading ? "Loading products..." : "Select Product"}
                </option>
                {!goodsLoading && goods.length === 0 && (
                  <option value="" disabled>No products available</option>
                )}
                {!goodsLoading && goods.map((item) => (
                  <option key={item.id} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>

              <input
                className="input-text"
                type="text"
                placeholder="Type"
                value={row.type}
                onChange={(e) => handleInputChange(row.id, "type", e.target.value)}
              />

              <div className="input-with-icon">
                <FiTruck size={13} />
                <input
                  className="input-number"
                  type="text"
                  min="0"
                  placeholder="0"
                  value={row.delivered}
                  onChange={(e) => handleInputChange(row.id, "delivered", e.target.value)}
                />
              </div>

              <div className="input-with-icon">
                <FiRotateCcw size={13} />
                <input
                  className="input-number"
                  type="text"
                  min="0"
                  placeholder="0"
                  value={row.received}
                  onChange={(e) => handleInputChange(row.id, "received", e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-delete"
                onClick={() => canDelete && handleRemoveRow(row.id)}
                disabled={!canDelete}
                aria-label="Remove row"
                title="Remove row"
              >
                <FiTrash2 size={15} />
              </button>
            </div>
          ))}
        </div>

        {/* ===== ADD PRODUCT BUTTON ===== */}
        <div className="add-product-section">
          <button type="button" className="btn-add" onClick={handleAddRow}>
            <FiPlus size={14} /> Add Another Product
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsertProduct;